import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Location, NgClass, NgFor, NgIf } from '@angular/common';

import { ToastrService } from 'ngx-toastr';

import { Art, ArtList } from '../../../models/classes/art.model';
import { MakePaymentMultiImagesComponent } from "../make-payment-multi-images/make-payment-multi-images.component";
import { UserCardComponent } from "../user-card/user-card.component";
import { ArtDetailCardComponent } from "../art-detail-card/art-detail-card.component";

import { StorageService } from '../../../services/state-management/storage.service/storage.service';
import { ExploreService } from '../../../services/API/explore.service/exploreservice';
import { ROLE } from '../../../models/constant/constants';
import { GuestLoginComponent } from '../../../modal/guest-login/guest-login.component';
import { RedirectManagerService } from '../../../services/state-management/redirect.service/redirect-manager.service';
import { Router } from '@angular/router';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-show-search-details',
  standalone: true,
  imports: [NgClass, NgFor, NgIf, MakePaymentMultiImagesComponent, UserCardComponent, ArtDetailCardComponent, GuestLoginComponent, ButtonComponent],
  templateUrl: './show-search-details.component.html',
  styleUrl: './show-search-details.component.scss',
})
export class ShowSearchDetailsComponent implements AfterViewInit {
  @Input() images: ArtList = new ArtList();
  @Input() btnText!: string;
  @Input() preselectedIds: number[] = []; // Explicitly declared property for preselected art IDs
  @Output() imagesUploaded = new EventEmitter<any>();
  @Output() selectionChanged = new EventEmitter<string[]>();
  @Output() close = new EventEmitter<void>();
  @Output() otherData = new EventEmitter<any>();
  @Output() fetchNextPage = new EventEmitter<void>();
  @Input() artistProfileId: string | null = '';
  @Input() accessToken: string | null = '';
  @Input() pageNo: number = 0;
  @Input() limit: number = 20; // Default limit for pagination
  @Input() isDisable?: boolean = true;
  @Input() searchTerm: string = '';
  @Input() totalArts: number = 0;
  @Input() noDataFound = '';

  isPayment:boolean = false;

  selectedArtList: ArtList = new ArtList();
  selectionOrder: Map<number, number> = new Map();
  role:ROLE = ROLE.GUEST

  @ViewChild('loadMoreTrigger', { static: false }) loadMoreTrigger!: ElementRef;
  isLoading = false; // To prevent duplicate API calls during scroll

  get artList():Art[] {
    return this.images as Art[];
  }

  constructor(
    private toaster:ToastrService,
    private storageService:StorageService,
    private exploreService: ExploreService,
    private location:Location,
    private redirectService: RedirectManagerService,
    private router:Router
  ){
    this.storageService.getRole().subscribe(role => this.role = role);
  }

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preselectedIds']) {
      this.setPreselectedIds();
    }
  }


  ngAfterViewInit() {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.isLoading) {
        if(this.images.length) {
          this.fetchNextPage.emit();
        }
      }
    },   {
      rootMargin: '300px',
      threshold: 0.5
    });

    observer.observe(this.loadMoreTrigger.nativeElement);
  }

  private setPreselectedIds(): void {
    const artListArray = this.preselectedIds.map(id =>
      this.images.find(image => image.id === id) || { id } // Keep a fallback
    );

    this.selectedArtList = new ArtList(...artListArray);
    this.selectionOrder.clear();
    this.selectedArtList.forEach((art, index) => {
      this.selectionOrder.set(art.id, index + 1);
    });

    // this.selectionChanged.emit(this.selectedArtList); // Emit initial selection
  }

  get selectedArtIDs(): Set<number> {
    return new Set(this.selectedArtList.map(el => el.id));
  }


  isOverflowing(): boolean {
      const scrollElement = this.scrollContainer.nativeElement;
      const scrollHeight = scrollElement.scrollHeight;
      const clientHeight = scrollElement.clientHeight;

      const overflow = clientHeight < scrollHeight;
      if(!overflow) {
        // this.fetchNextPage.emit()
      }
      return overflow;
  }


  clearSelection():void {
    this.selectedArtList = new ArtList();
    this.selectionOrder.clear();
  }


  toggleSelection(selectedArt: Art): void {
    // If art does not have price then don't allow user to select
    if(!(!!selectedArt.price)){
      this.toaster.info('Artwork has no price.')
      return;
    }

    // If current user tries to subscribe his own art then don't allow to select
    const artistId = this.storageService.getArtistId();
    if(selectedArt.artistId == artistId){
      this.toaster.info('You cannot subscribe your own artwork.')
      return;
    }

    // If art is already subscribed then don't allow to select
    if(selectedArt.subscribed){
      this.toaster.info('Artwork has already subscribed.')
      return;
    }


    // If art is already selected then unselect it

    const index = this.selectedArtList.findIndex(art => art.id === selectedArt.id);
    if (index > -1) {

      // Remove the image and delete its selection
      // order(index visible on the selection circle)

      this.selectedArtList.splice(index, 1);
      this.selectionOrder.delete(selectedArt.id);

      // Reassign selection order for all selected images
      this.selectedArtList.forEach((art, i) => {
        this.selectionOrder.set(art.id, i + 1);
      });
    } else {


      // Add the full image object and set its selection order
      this.selectedArtList.push(selectedArt);
      this.selectionOrder.set(selectedArt.id, this.selectedArtList.length);
    }
  }


  getSelectionOrder(artId: number): number {
    return this.selectionOrder.get(artId) || 0;
  }


  processSelectedImages() {
    if(this.role === ROLE.GUEST){
      this.isLoginPopupOpen = true;
      return;
    }
    this.otherData.emit(this.selectedArtList);
    this.isPayment = true;
  }

  back(): void {
    const url = this.redirectService.getRoute('onSubscribe');
    if(history && url) {
      this.redirectService.deleteRoute('onSubscribe');
      this.router.navigateByUrl('/gallery/explore', { replaceUrl: true });
      return;
    }
    this.location.back();
  }


  isArtDetailsOpen: boolean = false;
  artistDetail: any;
  openArtDetails(data: any) {
    this.isArtDetailsOpen = !this.isArtDetailsOpen;
    this.artistDetail = data;
  }

  onClickCloseArt() {
    this.isArtDetailsOpen = !this.isArtDetailsOpen;
    this.artistDetail = null;
  }

  onLikeClick(event: { id: any; likeUnlikeStatus: boolean; artistId:number }): void {
    this.exploreService.likeUnlikeArts(event.id, event.artistId).subscribe({
      next: (res:any) => {

      },
      error: (err: any) => {
        console.error('Failed to update like state:', err);
      },
    });
  }

  reset() {
    this.images = new ArtList();
    this.isPayment = false;
    this.selectedArtList = new ArtList();
    this.selectionOrder.clear();
  }

  isLoginPopupOpen = false;
  openLoginModal() {
    this.isLoginPopupOpen = true;
  }
}
